// Prepaid audit credits: buy upfront (like buying a credit); one is used when an audit
// engagement is opened. The ledger below is the record; the server owns the balance.
import { useEffect, useState } from 'react'
import { getAuditCredits, purchaseAuditCredits } from '../../api/kailux'
import { T } from '../../theme/theme'
import { auditErrorText, formatRand, formatDateTime } from '../../utils/audit'
import { Card, Field, Notice, Spinline, btn, inputStyle } from './ui'

const ENTRY = { purchase: 'Purchased', grant: 'Granted by KaiLux', consume: 'Used for an engagement', reversal: 'Reversal', adjustment: 'Adjustment' }

export default function CreditsPanel({ isAdminOfFirm, onChanged }) {
  const [data, setData] = useState(null)
  const [error, setError] = useState('')
  const [qty, setQty] = useState(1)
  const [busy, setBusy] = useState(false)
  const [done, setDone] = useState('')

  const load = () => getAuditCredits().then(setData).catch((e) => setError(auditErrorText(e, 'Could not load credits.')))
  useEffect(() => { load() }, [])

  const buy = async () => {
    setError(''); setDone(''); setBusy(true)
    try {
      const r = await purchaseAuditCredits(Number(qty))
      setDone(`Payment received. Your balance is now ${r.balance} credit${r.balance === 1 ? '' : 's'}.`)
      await load()
      onChanged && onChanged()
    } catch (e) {
      setError(auditErrorText(e, 'The payment could not be completed.'))
    } finally { setBusy(false) }
  }

  if (!data && !error) return <Card title="Audit credits"><Spinline /></Card>
  const price = data?.price_per_credit_rand
  const total = price ? price * (Number(qty) || 0) : null
  return (
    <Card testId="credits-card" title="Audit credits"
      subtitle="Each audit engagement uses one credit, taken when the engagement is opened. Credits are bought upfront and charged to the card on file.">
      {data && (
        <div style={{ display: 'flex', gap: '28px', flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: '14px' }}>
          <div>
            <div style={{ fontSize: '11px', fontWeight: 800, color: T.mt, letterSpacing: '0.06em' }}>BALANCE</div>
            <div data-testid="credit-balance" style={{ fontFamily: "'Syne',sans-serif", fontSize: '30px', fontWeight: 800, color: T.tx }}>{data.balance}</div>
          </div>
          <div>
            <div style={{ fontSize: '11px', fontWeight: 800, color: T.mt, letterSpacing: '0.06em' }}>PRICE PER CREDIT</div>
            <div data-testid="credit-price" style={{ fontSize: '18px', fontWeight: 700, color: T.tx }}>{price ? formatRand(price) : 'Not on sale yet'}</div>
          </div>
        </div>
      )}
      {price && isAdminOfFirm && (
        <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div style={{ width: '120px' }}>
            <Field label="Credits to buy">
              <input aria-label="Credits to buy" type="number" min="1" max="20" value={qty}
                onChange={(e) => setQty(e.target.value)} style={inputStyle} />
            </Field>
          </div>
          <button type="button" onClick={buy} disabled={busy || !(Number(qty) >= 1)} style={btn(true, busy)}>
            {busy ? 'Processing…' : `Buy for ${formatRand(total)}`}
          </button>
        </div>
      )}
      {price && !isAdminOfFirm && <div style={{ fontSize: '12px', color: T.mt }}>Only a firm administrator can buy credits.</div>}
      {error && <div style={{ marginTop: '12px' }}><Notice tone="bad" testId="credits-error">{error}</Notice></div>}
      {done && <div style={{ marginTop: '12px' }}><Notice tone="ok" testId="credits-done">{done}</Notice></div>}
      {data?.ledger?.length > 0 && (
        <table data-testid="credit-ledger" style={{ width: '100%', borderCollapse: 'collapse', marginTop: '16px', fontSize: '12px' }}>
          <thead><tr style={{ textAlign: 'left', color: T.mt }}>
            <th style={{ padding: '6px 4px' }}>When</th><th>Entry</th><th style={{ textAlign: 'right' }}>Credits</th><th style={{ textAlign: 'right' }}>Amount</th>
          </tr></thead>
          <tbody>
            {data.ledger.map((l) => (
              <tr key={l.id} style={{ borderTop: `1px solid ${T.bd}`, color: T.tx }}>
                <td style={{ padding: '6px 4px' }}>{formatDateTime(l.created_at)}</td>
                <td>{ENTRY[l.entry_type] || l.entry_type}</td>
                <td style={{ textAlign: 'right' }}>{l.credits > 0 ? `+${l.credits}` : l.credits}</td>
                <td style={{ textAlign: 'right' }}>{l.amount_rand ? formatRand(l.amount_rand) : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  )
}
